import React, { useCallback, useEffect, useState } from "react";
import { Inbox, Loader2, Send, Check, Bug, Lightbulb, Heart, MessageCircle, Building2, Mail, MapPinned, MapPin } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const KIND_ICON = { feedback: MessageCircle, contact: Mail, limits: Building2, demand: MapPinned, pin: MapPin };
const TYPE_ICON = { bug: Bug, idea: Lightbulb, compliment: Heart, other: MessageCircle };

// Everything a human sent us in one place: feedback from visitors, Contact Us enquiries and
// shops asking for higher limits — with the reply and the approval right next to the message.
export const AdminInbox = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("new");
  const [kind, setKind] = useState("all");
  const [replyFor, setReplyFor] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (status !== "all") p.set("status", status);
    if (kind !== "all") p.set("kind", kind);
    api.get(`/admin/inbox?${p.toString()}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [status, kind]);
  useEffect(load, [load]);

  const act = async (id, path, body, msg) => {
    setBusy(id + path);
    try {
      await api.post(`/admin/inbox/${id}/${path}`, body || {});
      toast({ title: msg });
      setReplyFor(null); setReply("");
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || "Action failed" }); }
    finally { setBusy(""); }
  };

  if (!data) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-inbox">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-foreground flex items-center gap-2"><Inbox size={18} className="text-[#16a34a]" /> Inbox</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Feedback, enquiries, limit requests and "deals wanted here" asks. {data.counts.new} new · {data.counts.replied} replied · {data.counts.closed} closed.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto min-w-0">
          <div className="w-full sm:w-auto overflow-x-auto no-scrollbar">
            <div className="inline-flex items-center gap-1 bg-secondary rounded-full p-1 w-max">
              {["new", "replied", "closed", "all"].map((s) => (
                <button key={s} onClick={() => setStatus(s)} data-testid={`inbox-status-${s}`}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold capitalize transition-all ${status === s ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="w-full sm:w-auto overflow-x-auto no-scrollbar">
            <div className="inline-flex items-center gap-1 bg-secondary rounded-full p-1 w-max">
              {["all", "feedback", "contact", "limits", "demand", "broadcast", "pin"].map((k) => (
                <button key={k} onClick={() => setKind(k)} data-testid={`inbox-kind-${k}`}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold capitalize transition-all ${kind === k ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>{k}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {data.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-4" data-testid="inbox-empty">Nothing here.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {data.rows.map((r) => {
            const KIcon = KIND_ICON[r.kind] || MessageCircle;
            const TIcon = TYPE_ICON[r.type] || null;
            return (
              <div key={r.id} className="border border-border rounded-xl p-3" data-testid={`inbox-row-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-secondary text-muted-foreground px-2 py-0.5 rounded-full capitalize">
                    <KIcon size={11} /> {r.kind}
                  </span>
                  {TIcon && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#16a34a]"><TIcon size={11} /> {r.type}</span>}
                  {r.priority && <span className="text-[11px] font-bold bg-[#16a34a] text-white px-2 py-0.5 rounded-full" data-testid={`inbox-priority-${r.id}`}>Elite priority</span>}
                  {r.status !== "new" && <span className="text-[11px] font-bold text-muted-foreground capitalize">{r.status}</span>}
                  <span className="text-[11px] text-muted-foreground ml-auto">{new Date(r.createdAt).toLocaleString()}</span>
                </div>

                <p className="text-sm font-bold text-foreground mt-1.5">{r.subject || r.name}</p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-0.5">{r.message || "(no message)"}</p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {r.name || "Visitor"}{r.email ? ` · ${r.email}` : " · no email"}{r.path ? ` · ${r.path}` : ""} · {r.planLabel}
                </p>
                {r.device && (
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5" data-testid={`inbox-device-${r.id}`}>{r.device}</p>
                )}

                {r.kind === "demand" && (
                  <p className="text-xs font-semibold text-foreground mt-1.5" data-testid={`inbox-demand-${r.id}`}>
                    Asked {r.asks || 1}× · {[r.place, r.country].filter(Boolean).join(", ") || "no place given"}
                  </p>
                )}

                {r.kind === "limits" && r.asked && (
                  <p className="text-xs font-semibold text-foreground mt-1.5" data-testid={`inbox-asked-${r.id}`}>
                    Asking for {r.asked.deals || "—"} live deals and {r.asked.branches || "—"} locations
                    {r.current ? ` (now ${r.current.deals ?? "∞"} / ${r.current.branches ?? "∞"})` : ""}
                    {r.granted ? ` · granted ${r.granted.deals || "—"} / ${r.granted.branches || "—"}` : ""}
                  </p>
                )}

                {(r.replies || []).map((rep, i) => (
                  <p key={i} className="text-xs text-muted-foreground mt-1.5 border-l-2 border-[#16a34a] pl-2 whitespace-pre-wrap">
                    {rep.message} <span className="text-[10px]">— {rep.by}, {new Date(rep.at).toLocaleDateString()}{rep.emailed ? "" : " (email failed)"}</span>
                  </p>
                ))}

                <div className="mt-2.5 flex flex-wrap gap-2">
                  {r.kind === "limits" && r.status !== "closed" && (
                    <button onClick={() => act(r.id, "approve-limits", {}, "Limits raised and shop told")}
                      disabled={busy === r.id + "approve-limits"} data-testid={`inbox-approve-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] text-white rounded-xl px-3 py-2 transition-colors">
                      {busy === r.id + "approve-limits" ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Approve what they asked
                    </button>
                  )}
                  {r.email && (
                    <button onClick={() => { setReplyFor(replyFor === r.id ? null : r.id); setReply(""); }} data-testid={`inbox-reply-btn-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:border-[#16a34a] rounded-xl px-3 py-2 transition-colors">
                      <Send size={13} /> Reply
                    </button>
                  )}
                  {r.status !== "closed" && (
                    <button onClick={() => act(r.id, "status", { status: "closed" }, "Closed")} disabled={busy === r.id + "status"}
                      data-testid={`inbox-close-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:bg-secondary rounded-xl px-3 py-2 transition-colors">
                      Close
                    </button>
                  )}
                  {r.status === "closed" && (
                    <button onClick={() => act(r.id, "status", { status: "new" }, "Reopened")} disabled={busy === r.id + "status"}
                      data-testid={`inbox-reopen-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:bg-secondary rounded-xl px-3 py-2 transition-colors">
                      Reopen
                    </button>
                  )}
                </div>

                {replyFor === r.id && (
                  <div className="mt-2.5">
                    <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} data-testid={`inbox-reply-text-${r.id}`}
                      placeholder={`Reply to ${r.email} — they get this by email`}
                      className="w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none resize-none" />
                    <button onClick={() => act(r.id, "reply", { message: reply }, "Reply sent")}
                      disabled={reply.trim().length < 2 || busy === r.id + "reply"} data-testid={`inbox-reply-send-${r.id}`}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold bg-[#0f2a1a] hover:bg-[#16341f] disabled:opacity-60 text-white rounded-xl px-4 py-2 transition-colors">
                      {busy === r.id + "reply" ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} Send reply
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminInbox;
